import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, AlertCircle, Settings2, QrCode, Play, Square, Puzzle, Camera, Trophy, Copy } from 'lucide-react';
import { useActivationTemplates } from '@/hooks/useActivationTemplates';
import {
  useActivations,
  useAttachActivation,
  useCustomizeActivation,
  useActivationShortCode,
  useStartActivationSession,
  useCloseActivationSession,
} from '@/hooks/useActivations';
import { ActivationStatusBadge } from '@/components/activations/statusBadges';
import { ActivationConfigForm } from '@/components/activations/ActivationConfigForm';
import { BrandingForm, normalizeBranding } from '@/components/activations/BrandingForm';
import { QrCodeCard } from '@/components/activations/QrCodeCard';
import { buildLeaderboardUrl } from '@/lib/activationsApiHelpers';
import { useToast } from '@/hooks/use-toast';
import {
  ACTIVATION_TYPE_LABELS,
  asActivationType,
  buildTypedConfig,
  readConfigValues,
  toConfigEnvelope,
  validateTypedConfig,
  type ConfigErrors,
  type TypedActivationConfig,
} from '@/types/activations';
import type {
  ActivationResponse,
  ActivationBrandingRequest,
  ActivationStatus,
  ActivationTemplateResponse,
} from '@/types/generated/activations.generated';

interface ActivationsTabProps {
  eventId: string;
}

const ACTIVATION_STATUSES: ActivationStatus[] = ['Draft', 'Live', 'Ended'];

export function ActivationsTab({ eventId }: ActivationsTabProps) {
  const { data: activations, isLoading, isError, error, refetch } = useActivations(eventId);
  const { data: templates } = useActivationTemplates();

  const [attachTemplateId, setAttachTemplateId] = useState<string>('');
  const [attachTemplate, setAttachTemplate] = useState<ActivationTemplateResponse | null>(null);
  const [customizing, setCustomizing] = useState<ActivationResponse | null>(null);
  const [qrFor, setQrFor] = useState<ActivationResponse | null>(null);
  const [leaderboardFor, setLeaderboardFor] = useState<ActivationResponse | null>(null);

  const templatesById = useMemo(() => {
    const map = new Map<string, ActivationTemplateResponse>();
    (templates ?? []).forEach((t) => map.set(t.id, t));
    return map;
  }, [templates]);

  const handleOpenAttach = () => {
    const template = templatesById.get(attachTemplateId);
    if (template) setAttachTemplate(template);
  };

  return (
    <div className="space-y-6" data-testid="activations-tab">
      <Card>
        <CardHeader>
          <CardTitle>Attach an activation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[260px] flex-1 space-y-1.5">
              <Select value={attachTemplateId} onValueChange={setAttachTemplateId}>
                <SelectTrigger data-testid="attach-template-select">
                  <SelectValue placeholder="Select a template…" />
                </SelectTrigger>
                <SelectContent>
                  {(templates ?? []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} · {ACTIVATION_TYPE_LABELS[t.activationType]}
                      {t.isGlobal ? ' (global)' : ' (org)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleOpenAttach} disabled={!attachTemplateId} data-testid="attach-activation-button">
              <Plus className="mr-2 h-4 w-4" />
              Configure & attach
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="space-y-2" data-testid="activations-loading">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      )}

      {isError && (
        <Alert variant="destructive" data-testid="activations-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load activations</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            {(error as Error)?.message || 'Something went wrong.'}
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && (activations?.length ?? 0) === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center" data-testid="activations-empty">
          <Puzzle className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No activations attached yet.</p>
        </Card>
      )}

      {!isLoading && !isError &&
        (activations ?? []).map((activation) => (
          <ActivationCard
            key={activation.id}
            eventId={eventId}
            activation={activation}
            template={templatesById.get(activation.activationTemplateId)}
            onCustomize={() => setCustomizing(activation)}
            onShowQr={() => setQrFor(activation)}
            onShowLeaderboard={() => setLeaderboardFor(activation)}
          />
        ))}

      {attachTemplate && (
        <AttachActivationDialog
          eventId={eventId}
          template={attachTemplate}
          displayOrder={activations?.length ?? 0}
          onClose={() => {
            setAttachTemplate(null);
            setAttachTemplateId('');
          }}
        />
      )}

      {customizing && (
        <CustomizeActivationDialog
          eventId={eventId}
          activation={customizing}
          template={templatesById.get(customizing.activationTemplateId)}
          onClose={() => setCustomizing(null)}
        />
      )}

      {qrFor && (
        <QrDialog eventId={eventId} activation={qrFor} onClose={() => setQrFor(null)} />
      )}

      {leaderboardFor && (
        <LeaderboardDialog
          eventId={eventId}
          activation={leaderboardFor}
          onClose={() => setLeaderboardFor(null)}
        />
      )}
    </div>
  );
}

interface ActivationCardProps {
  eventId: string;
  activation: ActivationResponse;
  template?: ActivationTemplateResponse;
  onCustomize: () => void;
  onShowQr: () => void;
  onShowLeaderboard: () => void;
}

function ActivationCard({
  eventId,
  activation,
  template,
  onCustomize,
  onShowQr,
  onShowLeaderboard,
}: ActivationCardProps) {
  const startSession = useStartActivationSession(eventId);
  const closeSession = useCloseActivationSession(eventId);
  const hasOpenSession = !!activation.activationSessionId;
  // BlazePod is operator-recorded; OperatorCurated photo booths also use the operator console.
  const isBlazePod = asActivationType(activation.activationType) === 'BlazePod';
  const configValues = readConfigValues(activation.config) as Record<string, unknown> | undefined;
  const isOperatorCurated =
    activation.activationType === 'PhotoBooth' &&
    configValues?.captureMode === 'OperatorCurated';
  // Both BlazePod and OperatorCurated photo booths expose the operator console.
  const hasOperatorConsole = isBlazePod || isOperatorCurated;

  return (
    <Card data-testid={`activation-card-${activation.id}`}>
      <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">
              {template?.name ?? ACTIVATION_TYPE_LABELS[activation.activationType]}
            </span>
            <ActivationStatusBadge status={activation.status} />
          </div>
          <p className="text-xs text-muted-foreground">
            {ACTIVATION_TYPE_LABELS[activation.activationType]} · {activation.family} · short code{' '}
            <span className="font-mono">{activation.shortCode}</span>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Session control applies to BOTH families — input activations (raffles) and
              generator activations (photo booths) are both openable/closeable. */}
          {hasOpenSession ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => closeSession.mutate(activation.id)}
              disabled={closeSession.isPending}
              data-testid={`close-session-${activation.id}`}
            >
              <Square className="mr-2 h-4 w-4" />
              Close session
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => startSession.mutate(activation.id)}
              disabled={startSession.isPending}
              data-testid={`start-session-${activation.id}`}
            >
              <Play className="mr-2 h-4 w-4" />
              Start session
            </Button>
          )}
          {hasOperatorConsole && (
            <Button variant="outline" size="sm" asChild data-testid={`operator-${activation.id}`}>
              <Link href={`/activations/events/${eventId}/operator?activationId=${activation.id}`}>
                <Camera className="mr-2 h-4 w-4" />
                Operator
              </Link>
            </Button>
          )}
          {isBlazePod && (
            <Button
              variant="outline"
              size="sm"
              onClick={onShowLeaderboard}
              data-testid={`leaderboard-${activation.id}`}
            >
              <Trophy className="mr-2 h-4 w-4" />
              Leaderboard
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onShowQr} data-testid={`qr-${activation.id}`}>
            <QrCode className="mr-2 h-4 w-4" />
            QR
          </Button>
          <Button variant="outline" size="sm" onClick={onCustomize} data-testid={`customize-${activation.id}`}>
            <Settings2 className="mr-2 h-4 w-4" />
            Customize
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface AttachDialogProps {
  eventId: string;
  template: ActivationTemplateResponse;
  displayOrder: number;
  onClose: () => void;
}

/**
 * Attach dialog: collects the REQUIRED typed config for the template's activation
 * type (prefilled from the template's DefaultConfig), validates it client-side, and
 * posts it nested under `config.values` so the backend handler accepts it.
 */
function AttachActivationDialog({ eventId, template, displayOrder, onClose }: AttachDialogProps) {
  const attach = useAttachActivation(eventId);
  const [config, setConfig] = useState<TypedActivationConfig>(() =>
    buildTypedConfig(template.activationType, readConfigValues(template.defaultConfig)),
  );
  const [branding, setBranding] = useState<ActivationBrandingRequest>({});
  const [submitted, setSubmitted] = useState(false);

  const errors = validateTypedConfig(template.activationType, config);
  const isValid = Object.keys(errors).length === 0;
  const shownErrors: ConfigErrors = submitted ? errors : {};

  const handleAttach = async () => {
    setSubmitted(true);
    if (!isValid) return;
    await attach.mutateAsync({
      activationTemplateId: template.id,
      config: toConfigEnvelope(config),
      branding: normalizeBranding(branding),
      displayOrder,
    });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="attach-dialog">
        <DialogHeader>
          <DialogTitle>Attach “{template.name}”</DialogTitle>
          <DialogDescription>
            {ACTIVATION_TYPE_LABELS[template.activationType]} — configure the required settings before
            attaching.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <h4 className="mb-3 text-sm font-semibold">Configuration</h4>
            <ActivationConfigForm
              type={template.activationType}
              value={config}
              onChange={setConfig}
              errors={shownErrors}
              eventId={eventId}
              idPrefix="attach"
            />
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold">Branding override (optional)</h4>
            <BrandingForm value={branding} onChange={setBranding} idPrefix="attach-brand" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAttach}
            disabled={!isValid || attach.isPending}
            data-testid="submit-attach"
          >
            {attach.isPending ? 'Attaching…' : 'Attach activation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CustomizeDialogProps {
  eventId: string;
  activation: ActivationResponse;
  template?: ActivationTemplateResponse;
  onClose: () => void;
}

function CustomizeActivationDialog({ eventId, activation, template, onClose }: CustomizeDialogProps) {
  const customize = useCustomizeActivation(eventId);
  // REQUIRED typed config — prefilled from the activation's stored config (or the
  // template default), read as the canonical `values` document (no slugifying).
  const [config, setConfig] = useState<TypedActivationConfig>(() =>
    buildTypedConfig(
      activation.activationType,
      readConfigValues(activation.config) ?? readConfigValues(template?.defaultConfig),
    ),
  );
  const [branding, setBranding] = useState<ActivationBrandingRequest>(activation.branding ?? {});
  const [status, setStatus] = useState<ActivationStatus>(activation.status);
  const [submitted, setSubmitted] = useState(false);

  const errors = validateTypedConfig(activation.activationType, config);
  const isValid = Object.keys(errors).length === 0;
  const shownErrors: ConfigErrors = submitted ? errors : {};

  const handleSave = async () => {
    setSubmitted(true);
    if (!isValid) return;
    await customize.mutateAsync({
      activationId: activation.id,
      data: {
        config: toConfigEnvelope(config),
        branding: normalizeBranding(branding),
        status,
      },
    });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="customize-dialog">
        <DialogHeader>
          <DialogTitle>Customize activation</DialogTitle>
          <DialogDescription>
            {template?.name ?? ACTIVATION_TYPE_LABELS[activation.activationType]} — edit the required
            configuration and optional branding.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="cust-status">Status</label>
            <Select value={status} onValueChange={(v) => setStatus(v as ActivationStatus)}>
              <SelectTrigger id="cust-status" data-testid="customize-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACTIVATION_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold">Configuration</h4>
            <ActivationConfigForm
              type={activation.activationType}
              value={config}
              onChange={setConfig}
              errors={shownErrors}
              eventId={eventId}
              idPrefix={`cust-${activation.id}`}
            />
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold">Branding override</h4>
            <BrandingForm value={branding} onChange={setBranding} idPrefix={`cust-brand-${activation.id}`} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || customize.isPending}
            data-testid="save-customize"
          >
            {customize.isPending ? 'Saving…' : 'Save customization'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QrDialog({ eventId, activation, onClose }: { eventId: string; activation: ActivationResponse; onClose: () => void }) {
  const { data, isLoading, isError } = useActivationShortCode(eventId, activation.id);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="qr-dialog">
        <DialogHeader>
          <DialogTitle>Activation QR code</DialogTitle>
          <DialogDescription>Participants scan this to open the runner.</DialogDescription>
        </DialogHeader>
        {isLoading && <Skeleton className="h-64 w-full" />}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Could not load the QR code</AlertTitle>
          </Alert>
        )}
        {data && <QrCodeCard url={data.runnerUrl ?? ''} shortCode={data.shortCode ?? ''} />}
      </DialogContent>
    </Dialog>
  );
}

/**
 * BlazePod leaderboard display dialog: shows the `/l/{shortCode}` URL the operator opens
 * on a nearby screen, with a copy affordance. The URL origin is derived from the
 * server-provided runner URL (same web base the QR uses).
 */
function LeaderboardDialog({
  eventId,
  activation,
  onClose,
}: {
  eventId: string;
  activation: ActivationResponse;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { data, isLoading, isError } = useActivationShortCode(eventId, activation.id);
  const leaderboardUrl = buildLeaderboardUrl(data?.runnerUrl, activation.shortCode);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(leaderboardUrl);
      toast({ title: 'Copied', description: 'Leaderboard URL copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: leaderboardUrl, variant: 'destructive' });
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="leaderboard-dialog">
        <DialogHeader>
          <DialogTitle>Leaderboard display</DialogTitle>
          <DialogDescription>
            Open this on the nearby screen to show the live top-scores leaderboard.
          </DialogDescription>
        </DialogHeader>
        {isLoading && <Skeleton className="h-16 w-full" />}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Could not load the leaderboard link</AlertTitle>
          </Alert>
        )}
        {!isLoading && (
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/40 p-3">
              <p className="break-all font-mono text-sm" data-testid="leaderboard-url">
                {leaderboardUrl}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copyUrl}
              data-testid="leaderboard-copy"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy URL
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
